import { DataGenMark } from './DataGenMark';

export interface BrandProps {
  size?: number;
  name?: string;
  /**
   * Optional secondary line. The string "A DataGen Product" (the default)
   * renders the DataGen lockup — icon + wordmark + "Product" subscript.
   * Any other string falls back to a plain text tagline. Pass `null` to
   * omit entirely.
   */
  tagline?: string | null;
  onClick?: () => void;
}

/** Wordmark + accent dot + DataGen lockup (or plain tagline) on the side. */
export function Brand({
  size = 16,
  name = 'LMcad',
  tagline = 'A DataGen Product',
  onClick,
}: BrandProps) {
  const interactive = !!onClick;
  const showDataGen = tagline === 'A DataGen Product';
  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (!interactive) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        cursor: interactive ? 'pointer' : 'default',
        userSelect: 'none',
        textTransform: 'none',
        letterSpacing: 'normal',
        /* Force the brand colour so DataGenMark (currentColor) doesn't
           inherit a muted slot colour like .slide-meta's var(--fg-subtle). */
        color: 'var(--fg)',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontWeight: 600,
          fontSize: size,
          letterSpacing: '-0.02em',
          color: 'var(--fg)',
        }}
      >
        {name}
      </span>
      {tagline && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            paddingLeft: 10,
            borderLeft: '1px solid var(--line)',
          }}
        >
          {showDataGen ? (
            /* Icon size chosen so the DataGen wordmark inside the lockup
               (≈ iconSize × 0.55) matches the LMcad wordmark font-size
               on the left. Keeps the two wordmarks the same height. */
            <DataGenMark size={Math.max(24, Math.round(size / 0.55))} />
          ) : (
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: Math.max(9, Math.round(size * 0.72)),
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--fg-faint)',
              }}
            >
              {tagline}
            </span>
          )}
        </span>
      )}
    </div>
  );
}
